import React, { useCallback, useEffect, useState } from 'react'
import { Table, Thead, Tbody, Tr, Th, Td, Text, Flex, Button, Tfoot } from '@chakra-ui/react'
import { EventSummary } from '../../services/network'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { createUsersApi } from '../../utils/api-interface'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { log } from '../../utils/logger'
import axios from 'axios'
import { RetryDialog } from '../RetryDialog/RetryDialog'
import { formatISODate } from '../../utils/utils'
import { EventStatusFilter, getEventSearchFilters, getEventStatusLabelKey } from '../../utils/event-status'

interface IEventTableProps {
  eventType: string
  userId: string
  startDate: string
  endDate: string
  selectedStatuses: EventStatusFilter[]
}

interface IPaginatedEventGeneralResponse {
  currentPage: number
  totalPages: number
  totalItems: number
  events: Array<EventSummary>
}

function EventTable({ eventType, userId, startDate, endDate, selectedStatuses }: IEventTableProps): JSX.Element {
  const [currentPage, setCurrentPage] = useState(1)
  const [eventResults, setEventResults] = useState<IPaginatedEventGeneralResponse>()

  const { t } = useTranslation()
  const [errorData, setErrorData] = useState<any>(null)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const handleCloseRetryDialog = (): void => undefined
  const navigate = useNavigate()

  useEffect(() => {
    setCurrentPage(1)
  }, [eventType, userId, startDate, endDate, selectedStatuses])

  const handleSearch = useCallback((): void => {
    const usersApi = createUsersApi()
    const filters = getEventSearchFilters(selectedStatuses)
    if (eventType === 'created') {
      usersApi
        .getUserEventsCreated(userId, startDate ? startDate : undefined, endDate ? endDate : undefined, filters.status, currentPage, 10, filters.noShowCheckIns)
        .then((response) => {
          setEventResults(response.data as unknown as IPaginatedEventGeneralResponse)
        })
        .catch((error) => {
          if (axios.isAxiosError(error)) {
            if (error.response) {
              log.error('Error fetching events created:', error.response.data)
              setErrorData(error.response.data)
            } else {
              log.error('No response from the server:', error.message)
            }
          } else {
            log.error('Unexpected error:', error)
          }
          setIsRetryDialogOpen(true)
        })
    }
    if (eventType === 'partecipated') {
      usersApi
        .getUserEventsReserved(
          userId,
          startDate ? startDate : undefined,
          endDate ? endDate : undefined,
          filters.status,
          currentPage,
          10,
          filters.noShowCheckIns
        )
        .then((response) => {
          setEventResults(response.data as unknown as IPaginatedEventGeneralResponse)
        })
        .catch((error) => {
          if (axios.isAxiosError(error)) {
            if (error.response) {
              log.error('Error fetching events partecipated:', error.response.data)
              setErrorData(error.response.data)
            } else {
              log.error('No response from the server:', error.message)
            }
          } else {
            log.error('Unexpected error:', error)
          }
          setIsRetryDialogOpen(true)
        })
    }
  }, [currentPage, endDate, eventType, selectedStatuses, startDate, userId])

  const handleConfirmRetryDialog = (): void => {
    handleSearch()
  }

  const handlePageChange = (page: number): void => {
    if (eventResults) {
      if (page > 0 && page <= eventResults?.totalPages) {
        setCurrentPage(page)
      }
    }
  }

  useEffect(() => {
    handleSearch()
  }, [eventType, userId, startDate, endDate, selectedStatuses, handleSearch])

  const goToEvent = useCallback(
    (id: string) => {
      navigate(`/users/userinfo/${userId}/${id}`)
    },
    [navigate, userId]
  )

  useEffect(() => {
    eva.replace()
  })

  return (
    <div>
      <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left' padding='26px'>
        {eventType === 'created' ? t('created_event') : t('reserved_event')}
      </Text>
      <Table bg={'white'}>
        <Thead height={'40px'}>
          <Th width='121px'>{t('unique_code')}</Th>
          <Th width={'512px'}>{t('event_name')}</Th>
          <Th width={'120px'}>{t('event_date')}</Th>
          <Th width={'90px'}>{t('status')}</Th>
          <Th width={'90px'}>{t('partecipant_number')}</Th>
          <Th width={'40px'}></Th>
        </Thead>
        <Tbody>
          {eventResults && eventResults.events ? (
            eventResults.events.map((event, index) => (
              <Tr height={'40px !important'} key={index} className='row'>
                <Td onClick={(): void => goToEvent(event.id)}>{event.id}</Td>
                <Td onClick={(): void => goToEvent(event.id)}>{event.name.substring(0, 45).concat(event.name.length > 45 ? '...' : '')}</Td>
                <Td onClick={(): void => goToEvent(event.id)}>{formatISODate(event.date)}</Td>
                <Td onClick={(): void => goToEvent(event.id)}>
                  <Text variant={event.status}>{t(getEventStatusLabelKey(event.status, event.successfulCheckInsCount))}</Text>
                </Td>
                <Td onClick={(): void => goToEvent(event.id)}>{event.participantCount}</Td>
                <Td>
                  <Link to={`/users/userinfo/${userId}/${event.id}`}>
                    <Button>
                      <i data-eva={'edit-2'} data-eva-fill={'#353F48'} data-eva-height='18' data-eva-width='16'></i>
                    </Button>
                  </Link>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={7} textAlign='center'>
                {t('table_no_results')}
              </Td>
            </Tr>
          )}
        </Tbody>
        <Tfoot>
          <Tr>
            <Td colSpan={7} padding={'0px'}>
              <Flex direction='row' justifyContent='space-between' alignItems='center' bg={'greyWarm100'}>
                <Flex direction='row' marginLeft='auto' alignItems='center'>
                  <Button onClick={(): void => handlePageChange(currentPage - 1)} isDisabled={currentPage === 1}>
                    <i data-eva={'chevron-left-outline'} data-eva-fill={'#353F48'}></i>
                  </Button>
                  <Button onClick={(): void => handlePageChange(currentPage + 1)} isDisabled={currentPage === eventResults?.totalPages}>
                    <i data-eva={'chevron-right-outline'} data-eva-fill={'#353F48'}></i>
                  </Button>
                </Flex>
              </Flex>
            </Td>
          </Tr>
        </Tfoot>
      </Table>
      <RetryDialog isOpen={isRetryDialogOpen} onClose={handleCloseRetryDialog} onConfirm={handleConfirmRetryDialog} message={errorData} />
    </div>
  )
}

export { EventTable }
